/**
 * Saved card positions (step 2.2 of docs/plans/canvas-layout-and-app-polish.md):
 * where a person put the cards by hand, on the setup canvas and on the
 * Business map. Only the cards moved are here; every other card is placed by
 * the automatic layout, and Tidy up re-runs that layout for those alone (D2).
 *
 * The setup's layout lives on its session, beside the draft (D1): a move is
 * never a draft version. Its keys are part paths (`record:<key>`). The map's
 * layout is the workspace's own, keyed by record and list ids; Build writes its
 * first one from the setup's (`mapLayoutOfSetup`, D3).
 *
 * Pure, so the server, the web and the phone read a layout one way.
 */

/** The automatic layout the positions were set against. A later engine bumps it. */
export const CARD_LAYOUT_VERSION = 1;

/** What one layout may hold: cards, a key's length, how far from the origin, and its JSON size. */
export const CARD_LAYOUT_LIMITS = { cards: 500, keyLength: 200, reach: 1_000_000, bytes: 65_536 } as const;

/** A card's top-left corner, in world units. */
export type CardPoint = { readonly x: number; readonly y: number };

/** The cards moved by hand, by key, and the engine they were moved against. */
export type CardLayout = { readonly version: number; readonly positions: Readonly<Record<string, CardPoint>> };

/**
 * `GET`/`PUT /v1/setup/sessions/:id/layout`. `revision` names the layout as
 * stored; a `PUT` that sends the one it last read or wrote is refused
 * LAYOUT_STALE (409) when the layout has moved on since (another device, or an
 * older write arriving late), so no move is lost unseen. A `PUT` with none
 * replaces whatever is there.
 */
export type SetupLayoutResponse = { readonly layout: CardLayout; readonly revision: string };

/** `GET`/`PUT /v1/businesses/:id/map-layout`: the layout, its `revision` (as the setup's), when it last moved, and whether the caller may move it. */
export type MapLayoutResponse = { readonly layout: CardLayout; readonly revision: string; readonly updatedAt: string | null; readonly mayMove: boolean };

/** The longest `revision` a write may send. */
const REVISION_LENGTH = 64;

/**
 * The `revision` a layout write was sent against: null when it sends none (the
 * write replaces whatever is there), else the text it was answered.
 */
export const readLayoutRevision = (raw: unknown): { ok: true; value: string | null } | CardLayoutRefusal => {
  const revision = raw !== null && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>).revision : undefined;
  if (revision === undefined || revision === null) return { ok: true, value: null };
  if (typeof revision !== 'string' || revision.length === 0 || revision.length > REVISION_LENGTH) {
    return { ok: false, code: 'LAYOUT_SHAPE', message: 'revision must be the text a layout read or write answered.' };
  }
  return { ok: true, value: revision };
};

export const emptyCardLayout = (): CardLayout => ({ version: CARD_LAYOUT_VERSION, positions: {} });

export type CardLayoutRefusal = { readonly ok: false; readonly code: 'LAYOUT_SHAPE' | 'LAYOUT_TOO_BIG'; readonly message: string };

/** A NUL, or half of a surrogate pair standing alone: Postgres's jsonb refuses either in a key, and neither names a part. */
const UNSTORABLE = /\u0000|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/;

/** A setup key: one part path segment, `kind:key` (`record:sales`), with no NUL or lone surrogate in it. */
export const isSetupLayoutKey = (key: string): boolean => /^[a-z_]+:[^/\s]+$/.test(key) && !UNSTORABLE.test(key);

/** A map key: a record's or list's id. */
export const isMapLayoutKey = (key: string): boolean => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(key);

const coordinate = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) && Math.abs(value) <= CARD_LAYOUT_LIMITS.reach ? Math.round(value * 10) / 10 : null;

const utf8 = new TextEncoder();

/** A JSON value as Postgres prints a jsonb (`::text`): a space after every colon and comma. */
const jsonbText = (value: unknown): string => {
  if (Array.isArray(value)) return `[${value.map(jsonbText).join(', ')}]`;
  if (value !== null && typeof value === 'object') return `{${Object.entries(value).map(([key, one]) => `${JSON.stringify(key)}: ${jsonbText(one)}`).join(', ')}}`;
  return JSON.stringify(value);
};

/**
 * A layout's size as the database measures it: the bytes of its jsonb text
 * (`octet_length(layout::text)`), which the setup session's and the map
 * layout's CHECKs hold to `bytes`. Compact JSON's length undercounts it: no
 * spaces, and characters rather than bytes.
 */
export const cardLayoutBytes = (layout: CardLayout): number => utf8.encode(jsonbText(layout)).length;

/**
 * A layout as a client sent it: `{ version, positions: { key: { x, y } } }`.
 * Each key must pass `keyOk`; each point is two finite numbers within reach,
 * kept to a tenth. More than `cards` cards, or more than `bytes` as the
 * database stores it (`cardLayoutBytes`), is LAYOUT_TOO_BIG; any other wrong
 * shape is LAYOUT_SHAPE.
 */
export const readCardLayout = (raw: unknown, keyOk: (key: string) => boolean): { ok: true; value: CardLayout } | CardLayoutRefusal => {
  const shape = (message: string): CardLayoutRefusal => ({ ok: false, code: 'LAYOUT_SHAPE', message });
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return shape('A layout is { version, positions }.');
  const body = raw as Record<string, unknown>;
  const version = body.version === undefined ? CARD_LAYOUT_VERSION : body.version;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1 || version > 1000) return shape('version must be a whole number from 1.');
  const positions = body.positions;
  if (positions === null || typeof positions !== 'object' || Array.isArray(positions)) return shape('positions must be an object from a card to { x, y }.');
  const entries = Object.entries(positions as Record<string, unknown>);
  if (entries.length > CARD_LAYOUT_LIMITS.cards) return { ok: false, code: 'LAYOUT_TOO_BIG', message: `A layout holds at most ${CARD_LAYOUT_LIMITS.cards} cards.` };
  // Kept in a Map and made an object at the end: a key such as `__proto__` is then an own key like any other.
  const read = new Map<string, CardPoint>();
  for (const [key, point] of entries) {
    if (key.length === 0 || key.length > CARD_LAYOUT_LIMITS.keyLength || !keyOk(key)) return shape(`${key.slice(0, 40)} names no card.`);
    if (point === null || typeof point !== 'object') return shape(`${key} must be { x, y }.`);
    const x = coordinate((point as Record<string, unknown>).x);
    const y = coordinate((point as Record<string, unknown>).y);
    if (x === null || y === null) return shape(`${key} must be two finite numbers within ${CARD_LAYOUT_LIMITS.reach} of the origin.`);
    read.set(key, { x, y });
  }
  const value: CardLayout = { version, positions: Object.fromEntries(read) };
  if (cardLayoutBytes(value) > CARD_LAYOUT_LIMITS.bytes) return { ok: false, code: 'LAYOUT_TOO_BIG', message: `A layout is at most ${CARD_LAYOUT_LIMITS.bytes} bytes.` };
  return { ok: true, value };
};

/** A layout read back from storage: whatever does not read is no layout, never an error. */
export const storedCardLayout = (raw: unknown, keyOk: (key: string) => boolean): CardLayout => {
  const read = readCardLayout(raw, keyOk);
  return read.ok ? read.value : emptyCardLayout();
};

/**
 * The map's first layout, from the setup's (D3): each `record:<key>` position
 * at the id that record or list was built as. A card the build did not make,
 * and any part that is not a record or list, is left to the automatic layout.
 */
export const mapLayoutOfSetup = (layout: CardLayout, recordIds: Readonly<Record<string, string>>): CardLayout => {
  // Own keys only: `record:constructor` is no record, whatever an object inherits.
  const positions = new Map<string, CardPoint>();
  for (const [path, point] of Object.entries(layout.positions)) {
    if (!path.startsWith('record:')) continue;
    const key = path.slice('record:'.length);
    const id = Object.hasOwn(recordIds, key) ? recordIds[key] : undefined;
    if (typeof id === 'string') positions.set(id, point);
  }
  return { version: layout.version, positions: Object.fromEntries(positions) };
};
